import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from './components/ConfirmationDialog';
import DropdownMenu from './components/DropdownMenu';
import RenameProjectModal from './components/RenameProjectModal';
import AddProjectModal from './components/AddProjectModal';
import CreateMasterProjectModal from './CreateMasterProjectModal';
import MasterProjectCard from './MasterProjectCard';
import MoveProjectModal from './MoveProjectModal';
import { API_URL, LIVE_API_URL, LOCAL_API_URL } from './Config';

function ProjectListPage({ onLogout }) {
  const [masterProjects, setMasterProjects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [expandedMasterProjects, setExpandedMasterProjects] = useState(new Set());
  const [showCreateMasterProjectModal, setShowCreateMasterProjectModal] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [projectToRename, setProjectToRename] = useState(null);
  const [showMoveProjectModal, setShowMoveProjectModal] = useState(false);
  const [projectToMove, setProjectToMove] = useState(null);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [selectedMasterProjectId, setSelectedMasterProjectId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [masterProjectsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingDB, setUploadingDB] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMasterProjects();
    fetchProjects();
  }, []);

  const fetchMasterProjects = async (preserveExpandedState = false, searchQuery = searchTerm, page = currentPage) => {
    setIsLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: masterProjectsPerPage.toString()
      });
      
      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      const response = await fetch(`${API_URL}/master-projects?${params}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // The API returns {master_projects: [...], pagination: {...}}
      const masterProjectsArray = data.master_projects || [];
      setMasterProjects(masterProjectsArray);
      setPagination(data.pagination || {
        page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      });
      
      // Start with all master projects closed by default
      if (!preserveExpandedState) {
        setExpandedMasterProjects(new Set());
      }
      // If preserveExpandedState is true, keep the current expanded state
    } catch (error) {
      console.error("Error fetching master projects:", error);
      setMasterProjects([]); // Set to empty array on error
      setPagination({
        page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API_URL}/projects`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const handleCreateMasterProject = async (masterProjectData) => {
    console.log('handleCreateMasterProject called with:', masterProjectData);
    try {
      console.log('Sending POST request to:', `${API_URL}/master-projects`);
      const response = await fetch(`${API_URL}/master-projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(masterProjectData),
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const newMasterProject = await response.json();
      console.log('New master project created:', newMasterProject);
      
      // Update master projects list and auto-expand the new master project
      await fetchMasterProjects(true);
      setShowCreateMasterProjectModal(false);
      
      // Auto-expand the newly created master project
      setExpandedMasterProjects(prev => new Set([...prev, newMasterProject.id]));
    } catch (error) {
      console.error("Error creating master project:", error);
      alert('Failed to create master project: ' + error.message);
    }
  };

  const handleCreateProject = (masterProjectId) => {
    setSelectedMasterProjectId(masterProjectId);
    setShowAddProjectModal(true);
  };

  const handleAddProject = async (projectData) => {
    try {
      const response = await fetch(`${API_URL}/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: projectData.title,
          description: projectData.description,
          master_project_id: projectData.masterProjectId,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      // Refresh both master projects and projects while preserving expanded state
      await fetchMasterProjects(true);
      await fetchProjects();
      setShowAddProjectModal(false);
    } catch (error) {
      console.error("Error creating project:", error);
      throw error; // Re-throw to let modal handle the error display
    }
  };

  const handleCancelAddProject = () => {
    setShowAddProjectModal(false);
    setSelectedMasterProjectId(null);
  };

  const handleUpdateMasterProject = async (masterProjectId, updateData) => {
    try {
      const response = await fetch(`${API_URL}/master-projects/${masterProjectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      await fetchMasterProjects(true);
    } catch (error) {
      console.error("Error updating master project:", error);
      throw error;
    }
  };

  const handleDeleteMasterProject = async (masterProjectId) => {
    try {
      const response = await fetch(`${API_URL}/master-projects/${masterProjectId}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      await fetchMasterProjects(true);
      await fetchProjects();
    } catch (error) {
      console.error("Error deleting master project:", error);
      throw error;
    }
  };

  const handleToggleMasterProjectExpanded = (masterProjectId) => {
    setExpandedMasterProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(masterProjectId)) {
        newSet.delete(masterProjectId);
      } else {
        newSet.add(masterProjectId);
      }
      return newSet;
    });
  };

  const getProjectsForMasterProject = (masterProjectId) => {
    return projects.filter(project => project.master_project_id === masterProjectId);
  };

  // Debounced search to avoid too many API calls
  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setCurrentPage(1); // Reset to page 1 when search changes
    
    // Debounce the API call
    setTimeout(() => {
      // Check current input value to see if it matches when timeout executes
      const currentInputValue = document.querySelector('.search-input')?.value;
      if (currentInputValue === newSearchTerm) { // Only make API call if search term hasn't changed
        fetchMasterProjects(false, newSearchTerm, 1);
      }
    }, 500); // Increased to 500ms for better UX
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchMasterProjects(true, searchTerm, pageNumber); // Preserve expanded state during pagination
  };

  const handleSelectProject = (projectId) => {
    navigate(`/project/${projectId}`);
  };

  const handleViewVideos = (e, projectId) => {
    e.stopPropagation(); // Prevent event bubbling
    navigate(`/project/${projectId}/videos`);
  };

  const handleNavigateToLearning = (e, projectId) => {
    e.stopPropagation(); // Prevent event bubbling
    navigate(`/project/${projectId}/learning`);
  };

  const handleNavigateToMasterProjectLearning = (e, masterProjectId) => {
    e.stopPropagation(); // Prevent event bubbling
    navigate(`/master-project/${masterProjectId}/learning`);
  };

  const handleSyncMedia = async (e, projectId) => {
    e.stopPropagation(); // Prevent event bubbling
    
    try {
      console.log('🔘 Download Media button clicked!', { projectId });
      
      if (!window.confirm(`Download media files from live server for this project?\n\nThis will download all videos, screenshots, and audio files to your local storage. This may take several minutes depending on file sizes.`)) {
        console.log('🚫 User cancelled sync');
        return;
      }

      console.log(`🔄 Starting media download for project ${projectId}...`);
      console.log(`📡 Making request to: ${LOCAL_API_URL}/sync-media/${projectId}`);

      const response = await fetch(`${LOCAL_API_URL}/sync-media/${projectId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Media sync completed:', result);

      const stats = result.statistics || {};
      const downloaded = stats.downloaded_new || 0;
      const existing = stats.updated_existing || 0;
      const failed = stats.failed || 0;
      const total = stats.total_scanned || 0;
      
      if (failed === 0) {
        alert(`✅ Media sync completed successfully!\n\n📥 Downloaded: ${downloaded} new files\n✅ Already existed: ${existing} files\n📊 Total processed: ${total} files\n🎉 Project data synced: ${result.project_sync?.project_synced ? 'Yes' : 'No'}`);
      } else {
        alert(`⚠️ Media sync completed with some issues.\n\n📥 Downloaded: ${downloaded} files\n✅ Already existed: ${existing} files\n❌ Failed: ${failed} files\n📊 Total processed: ${total} files\n\nCheck console for details.`);
        console.error('Sync details:', result);
      }

    } catch (err) {
      console.error('❌ Media download error:', err);
      console.error('❌ Error details:', { name: err.name, message: err.message, stack: err.stack });
      alert(`❌ Media download failed: ${err.message}\n\nPlease try again or check the console for more details.`);
    }
  };

  const handleDeleteProject = async (e, projectId, projectTitle) => {
    e.stopPropagation(); // Prevent triggering handleSelectProject
    setProjectToDelete({ id: projectId, title: projectTitle });
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      const response = await fetch(`${API_URL}/projects/${projectToDelete.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setProjects((prevProjects) => prevProjects.filter((project) => project.id !== projectToDelete.id));
      setProjectToDelete(null);
      setShowConfirmDialog(false);
    } catch (error) {
      console.error("Error deleting project:", error);
      alert("Failed to delete project. Please try again.");
      setProjectToDelete(null);
      setShowConfirmDialog(false);
    }
  };

  const cancelDelete = () => {
    setProjectToDelete(null);
    setShowConfirmDialog(false);
  };

  const handleDuplicateProject = async (e, projectId) => {
    e.stopPropagation(); // Prevent triggering handleSelectProject
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await response.json();
      await fetchMasterProjects(true);
      await fetchProjects(); // Refresh the list after duplicating
    } catch (error) {
      console.error("Error duplicating project:", error);
      alert("Failed to duplicate project. Please try again.");
    }
  };

  const handleRenameProject = (e, project) => {
    e.stopPropagation();
    setProjectToRename(project);
    setShowRenameModal(true);
  };

  const confirmRename = async (updatedData) => {
    if (!projectToRename) return;

    try {
      console.log("Attempting to rename project:", projectToRename.id, "with data:", updatedData);
      console.log("API URL:", `${API_URL}/projects/${projectToRename.id}`);
      
      const response = await fetch(`${API_URL}/projects/${projectToRename.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData),
      });
      
      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server error response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const result = await response.json();
      console.log("Rename successful:", result);
      
      await fetchMasterProjects(true);
      await fetchProjects(); // Refresh the list after renaming
      setShowRenameModal(false);
      setProjectToRename(null);
    } catch (error) {
      console.error("Error renaming project:", error);
      alert(`Failed to rename project: ${error.message}`);
    }
  };

  const cancelRename = () => {
    setShowRenameModal(false);
    setProjectToRename(null);
  };

  const handleMoveProject = async (e, project) => {
    e.stopPropagation();
    setProjectToMove(project);
    setShowMoveProjectModal(true);
  };

  const confirmMoveProject = async (projectId, newMasterProjectId) => {
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/move`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          master_project_id: newMasterProjectId,
          sort_order: 1 // Place at the beginning of the new master project
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      // Refresh both master projects and projects while preserving expanded state
      await fetchMasterProjects(true);
      await fetchProjects();
      setShowMoveProjectModal(false);
      setProjectToMove(null);
    } catch (error) {
      console.error("Error moving project:", error);
      alert(`Failed to move project: ${error.message}`);
    }
  };

  const cancelMoveProject = () => {
    setShowMoveProjectModal(false);
    setProjectToMove(null);
  };

  const handleReorderProject = async (masterProjectId, projectId, newPosition) => {
    try {
      // Get current projects for this master project
      const currentProjects = getProjectsForMasterProject(masterProjectId);
      const sortedCurrentProjects = [...currentProjects].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      
      // Find the project to move
      const projectIndex = sortedCurrentProjects.findIndex(p => p.id === projectId);
      if (projectIndex === -1) return;
      
      // If position hasn't changed, do nothing
      if (projectIndex === newPosition) return;
      
      // Create a new array with the project moved to the new position
      const reorderedProjects = [...sortedCurrentProjects];
      const [movedProject] = reorderedProjects.splice(projectIndex, 1);
      reorderedProjects.splice(newPosition, 0, movedProject);
      
      // Extract just the project IDs in the new order
      const orderedProjectIds = reorderedProjects.map(project => project.id);

      // Use the dedicated reorder endpoint
      const response = await fetch(`${API_URL}/master-projects/${masterProjectId}/projects/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          project_ids: orderedProjectIds
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      // Refresh the data while preserving expanded state
      await fetchMasterProjects(true);
      await fetchProjects();
    } catch (error) {
      console.error("Error reordering project:", error);
      alert(`Failed to reorder project: ${error.message}`);
    }
  };

  const handleDownloadDB = async () => {
    try {
      // Download from live server to get complete database
      const response = await fetch(`${LIVE_API_URL}/download-db`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Check if response is JSON (old format) or SQL (new format)
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        // Handle old JSON format from live server
        const jsonData = await response.json();
        
        // Create a blob with the JSON data
        const jsonString = JSON.stringify(jsonData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        
        // Create download link
        const a = document.createElement('a');
        a.href = url;
        a.download = `database_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Show success message with export info
        const exportInfo = jsonData.export_info;
        alert(`Database exported successfully from live server!\\n\\nExported: ${exportInfo.total_tables || 'multiple'} tables with ${exportInfo.total_rows || 'many'} total rows\\nExport time: ${exportInfo.export_timestamp || new Date().toISOString()}`);
      } else {
        // Handle new SQL format
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `database_backup_${new Date().toISOString().split('T')[0]}.sql`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        alert('Database exported successfully from live server as SQL file!');
      }
    } catch (error) {
      console.error("Error downloading database:", error);
      alert(`Failed to download database: ${error.message}`);
    }
  };

  const handleUploadDB = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.sql,.json';  // Accept both SQL and JSON files
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const isSQL = file.name.endsWith('.sql');
      const isJSON = file.name.endsWith('.json');
      
      if (!isSQL && !isJSON) {
        alert('Please select a .sql or .json file (database backup)');
        return;
      }

      const confirmed = window.confirm(
        'This will completely replace your local database with the uploaded data. ' +
        'All existing data will be lost. Are you sure you want to continue?'
      );

      if (!confirmed) return;

      setUploadingDB(true);
      try {
        const formData = new FormData();
        
        // Use appropriate parameter name based on file type
        if (isSQL) {
          formData.append('sql_file', file);
        } else {
          formData.append('json_file', file);
        }

        // Always upload to LOCAL server
        const response = await fetch(`${LOCAL_API_URL}/upload-db`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }

        const result = await response.json();
        console.log('Database upload result:', result);
        
        // Create detailed success message
        let message = `Database uploaded and restored successfully from ${isSQL ? 'SQL' : 'JSON'} backup to local server!\\n\\n`;
        
        if (result.stats) {
          message += `📊 Import Stats:\\n`;
          message += `• Tables restored: ${result.stats.table_count}\\n`;
          message += `• Total rows: ${result.stats.total_rows}\\n`;
          message += `• Import time: ${result.stats.import_timestamp}\\n\\n`;
        }
        
        if (result.stats && result.stats.tables && result.stats.tables.length > 0) {
          message += `📋 Restored tables:\\n`;
          message += result.stats.tables.map(table => 
            `• ${table}: ${result.stats.table_counts[table] || 0} rows`
          ).join('\\n');
          
          if (result.stats.total_rows === 0) {
            message += '\\n\\n⚠️ Note: Database structure was restored but contains no data.';
          }
        }
        
        alert(message);
        
        // Refresh the projects list
        await fetchMasterProjects(true);
        await fetchProjects();
      } catch (error) {
        console.error("Error uploading database:", error);
        alert(`Failed to upload database: ${error.message}`);
      } finally {
        setUploadingDB(false);
      }
    };
    input.click();
  };

  const handleClearDB = async () => {
    const confirmed = window.confirm(
      'This will completely wipe your local database, removing ALL data permanently. ' +
      'Are you absolutely sure you want to continue?'
    );

    if (!confirmed) return;

    const doubleConfirmed = window.confirm(
      'Last warning: This action cannot be undone. All projects, timeline items, and data will be lost. Continue?'
    );

    if (!doubleConfirmed) return;

    setUploadingDB(true);
    try {
      const response = await fetch(`${API_URL}/clear-db`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const result = await response.json();
      console.log('Clear DB result:', result);

      if (result.success) {
        alert(`Database cleared successfully!\n\nDropped:\n- ${result.dropped.tables.length} tables\n- ${result.dropped.sequences.length} sequences\n- ${result.dropped.functions.length} functions`);
      } else {
        alert(`Database clearing incomplete:\n\nRemaining:\n- ${result.remaining.tables.length} tables\n- ${result.remaining.sequences.length} sequences\n- ${result.remaining.functions.length} functions`);
      }
      
      // Refresh the projects list
      await fetchMasterProjects(true);
      await fetchProjects();
    } catch (error) {
      console.error("Error clearing database:", error);
      alert(`Failed to clear database: ${error.message}`);
    } finally {
      setUploadingDB(false);
    }
  };


  return (
    <div className="project-list-page-container">
      <div className="project-list-header">
        <h1 className="project-list-page-title">Project List</h1>
        <div className="header-actions">
          {/* DB Operations Dropdown */}
          {window.location.hostname === 'localhost' && (
            <div className="db-dropdown">
              <DropdownMenu 
                trigger={
                  <button className="db-operations-button" title="Database Operations - Download, Upload & Clear">
                    DB Tasks ▼
                  </button>
                }
              >
                {API_URL === 'https://api-guru.e-so.in' && (
                  <button className="dropdown-item" onClick={handleDownloadDB}>
                    ↓ Download DB
                  </button>
                )}
                {API_URL === 'http://localhost:8000' && (
                  <>
                    <button 
                      className="dropdown-item" 
                      onClick={handleClearDB}
                      disabled={uploadingDB}
                    >
                      × {uploadingDB ? 'Clearing...' : 'Clear DB'}
                    </button>
                    <button 
                      className="dropdown-item" 
                      onClick={handleUploadDB}
                      disabled={uploadingDB}
                    >
                      ↑ {uploadingDB ? 'Uploading...' : 'Upload DB'}
                    </button>
                  </>
                )}
              </DropdownMenu>
            </div>
          )}
          
          {/* Settings (YouTube Linking) */}
          <button 
            onClick={() => navigate('/youtube-linking')}
            className="settings-button"
            title="Settings - YouTube Linking & Configuration"
          >
            Settings
          </button>
          
          {/* Logout */}
          {onLogout && (
            <button 
              onClick={onLogout}
              className="logout-button"
              title="Logout - Sign out of application"
            >
              Logout
            </button>
          )}
        </div>
      </div>
      
      <div className="master-project-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search master projects..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
        <button
          onClick={() => setShowCreateMasterProjectModal(true)}
          className="create-master-project-button"
        >
          + Create Master Project
        </button>
      </div>

      <div className="master-projects-section">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading master projects...</p>
          </div>
        ) : !Array.isArray(masterProjects) || masterProjects.length === 0 ? (
          <p className="no-projects-message">
            {searchTerm ? 'No master projects match your search.' : 'No master projects yet. Create one above!'}
          </p>
        ) : (
          <div className="master-projects-list">
            {masterProjects.map((masterProject) => (
              <MasterProjectCard
                key={masterProject.id}
                masterProject={masterProject}
                projects={getProjectsForMasterProject(masterProject.id)}
                isExpanded={expandedMasterProjects.has(masterProject.id)}
                onToggleExpanded={() => handleToggleMasterProjectExpanded(masterProject.id)}
                onUpdateMasterProject={handleUpdateMasterProject}
                onDeleteMasterProject={handleDeleteMasterProject}
                onCreateProject={handleCreateProject}
                onNavigateToProject={handleSelectProject}
                onMoveProject={handleMoveProject}
                onViewVideos={handleViewVideos}
                onRenameProject={handleRenameProject}
                onDuplicateProject={handleDuplicateProject}
                onDeleteProject={handleDeleteProject}
                onReorderProject={handleReorderProject}
                onSyncMedia={handleSyncMedia}
                onNavigateToLearning={handleNavigateToLearning}
                onNavigateToMasterProjectLearning={handleNavigateToMasterProjectLearning}
              />
            ))}
          </div>
        )}

        {pagination.total_pages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {((pagination.page - 1) * pagination.per_page) + 1}-{Math.min(pagination.page * pagination.per_page, pagination.total_count)} of {pagination.total_count} master projects
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.has_prev}
                className="pagination-button"
              >
                Previous
              </button>
              
              {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`pagination-button ${pagination.page === pageNumber ? 'active' : ''}`}
                >
                  {pageNumber}
                </button>
              ))}
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        title="Confirm Deletion"
        message={projectToDelete ? `Are you sure you want to delete the project "${projectToDelete.title}"? This action cannot be undone.` : ''}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      
      <RenameProjectModal
        isOpen={showRenameModal}
        project={projectToRename}
        onRename={confirmRename}
        onCancel={cancelRename}
      />
      
      <CreateMasterProjectModal
        isOpen={showCreateMasterProjectModal}
        onSubmit={handleCreateMasterProject}
        onClose={() => setShowCreateMasterProjectModal(false)}
      />
      
      <MoveProjectModal
        isOpen={showMoveProjectModal}
        project={projectToMove}
        masterProjects={masterProjects}
        onMove={confirmMoveProject}
        onCancel={cancelMoveProject}
      />

      <AddProjectModal
        isOpen={showAddProjectModal}
        masterProjectId={selectedMasterProjectId}
        onAdd={handleAddProject}
        onCancel={handleCancelAddProject}
      />
    </div>
  );
}

export default ProjectListPage;